import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { MembershipTierResponse, Post, PracticeStats, Product } from "@repo/shared-types";
import { useAuth } from "../lib/auth-context";

interface HomeState {
  posts: Post[];
  products: Product[];
  stats: PracticeStats | null;
  membership: MembershipTierResponse | null;
}

const EMOJIS = ["📖", "🧘", "✨", "🕉️", "📿", "🌿"];
const SIGNAL_ITEMS = [
  { icon: "🧘", text: "4,200 practitioners meditated today" },
  { icon: "📚", text: "128 new wisdom reads in the last hour" },
  { icon: "🌙", text: "Dream circle opens in 2 hours" },
  { icon: "🏅", text: "89 seekers unlocked consistency badges" },
  { icon: "🎟️", text: "New retreat pass drop this weekend" },
  { icon: "🌍", text: "Live community now across 120+ countries" },
];
const SIGNAL_ITEMS_LOOP = [...SIGNAL_ITEMS, ...SIGNAL_ITEMS];

export function HomePage() {
  const { user } = useAuth();
  const [state, setState] = useState<HomeState>({ posts: [], products: [], stats: null, membership: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [postsRes, productsRes, statsRes, membershipRes] = await Promise.all([
          api.listPosts({ per_page: "6" }),
          api.listProducts({ limit: "4", sort: "date_desc" }),
          user ? api.getPracticeStats() : Promise.resolve(null),
          user ? api.getMembershipTier() : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setState({ posts: postsRes.posts, products: productsRes.products, stats: statsRes, membership: membershipRes });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user]);

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="hero">
        <div className="container">
          <div className="hero__grid">
            <div className="hero__inner">
              <span className="hero__badge">✦ Enlightenment Through Dreams</span>
              <h1 className="hero__title">
                {"Ancient wisdom\u00A0for "}<em>modern seekers</em>
              </h1>
              <p className="hero__body">
                Transform your life with timeless spiritual teachings adapted for modern living.
                Track your daily practice, explore curated wisdom, and join a global community
                on the path to Aekam.
              </p>
              <div className="hero__ctas">
                <Link to={user ? "/practice" : "/login"} className="btn btn--gold btn--lg">
                  {user ? "Continue Practice" : "Begin Your Journey"}
                </Link>
                <Link to="/wisdom" className="btn btn--outline-light btn--lg">
                  Explore Wisdom
                </Link>
              </div>
            </div>

            <div className="hero-visual" aria-hidden="true">
              <div className="hero-visual__aura" />
              <div className="hero-visual__burst" />
              <div className="hero-visual__beam" />
              <div className="hero-visual__rays">
                {[...Array(8)].map((_, i) => (
                  <span 
                    key={i} 
                    style={{ 
                      "--hero-ray-rotate": `${(i - 3.5) * 15}deg`, 
                      "--hero-ray-delay": `${i * 0.5}s` 
                    } as React.CSSProperties} 
                  />
                ))}
              </div>
              <div className="hero-visual__halo" />

              <picture className="hero-hand hanuman-hand">
                <source srcSet="/assets/hero-divine-hand-wow.webp" type="image/webp" />
                <img
                  src="/assets/hero-divine-hand-wow.png"
                  alt=""
                  aria-hidden="true"
                  loading="eager"
                  decoding="async"
                />
              </picture>
            </div>
          </div>

          <div className="hero__stats">
            {[
              { value: "50K+", label: "Active Members" },
              { value: "1M+", label: "Daily Practices" },
              { value: "120+", label: "Countries" },
              { value: "2K+", label: "Wisdom Articles" },
            ].map((s) => (
              <div key={s.label}>
                <span className="hero-stat__value">{s.value}</span>
                <span className="hero-stat__label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Live Signal Rail ─────────────────────────────────── */}
      <section className="signal-rail" aria-label="Community pulse">
        <div className="signal-rail__viewport">
          <div className="signal-rail__track">
            {SIGNAL_ITEMS_LOOP.map((item, idx) => (
              <span key={`${item.text}-${idx}`} className="signal-pill">
                <span aria-hidden="true">{item.icon}</span>
                <span>{item.text}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Logged-in stats strip ─────────────────────────────── */}
      {user && state.stats && state.membership && (
        <section className="stats-strip">
          <div className="container">
            <div className="stats-strip__grid">
              <div>
                <span className="stats-strip__value">{state.stats.current_streak}</span>
                <span className="stats-strip__label">Day streak</span>
              </div>
              <div>
                <span className="stats-strip__value">{state.stats.total_minutes}</span>
                <span className="stats-strip__label">Minutes logged</span>
              </div>
              <div>
                <span className="stats-strip__value">{state.stats.total_sessions}</span>
                <span className="stats-strip__label">Sessions completed</span>
              </div>
              <div>
                <span className="stats-strip__value">{state.membership.tier.toUpperCase()}</span>
                <span className="stats-strip__label">Membership tier</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Features ──────────────────────────────────────────── */}
      <section className="section section--surface">
        <div className="container">
          <div className="section-header--center">
            <span className="eyebrow eyebrow--blue">Everything you need</span>
            <h2 className="display-3 section-header__title-spaced">
              A complete spiritual platform
            </h2>
            <p className="body-lg text-muted section-header__body-spaced">
              One thoughtful home for practice, wisdom, and conscious commerce — built for daily return.
            </p>
          </div>

          <div className="features-grid">
            <FeatureCard icon="🧘" iconClass="feature-card__icon--gold"   title="Daily Practice Tracker"  description="Log meditations, yoga, and breathwork. Build streaks, track momentum, and visualise weekly consistency."    href={user ? "/practice" : "/login"} cta="Start practicing" />
            <FeatureCard icon="📚" iconClass="feature-card__icon--blue"   title="Wisdom Library"          description="Browse thousands of teachings, guided reflections, and courses curated for every stage of the journey."   href="/wisdom"                       cta="Explore wisdom" />
            <FeatureCard icon="🛍️" iconClass="feature-card__icon--green"  title="Sacred Commerce"         description="Discover spiritual products, membership passes, and premium content — woven into your practice flow."    href="/shop"                         cta="Browse products" />
            <FeatureCard icon="🌙" iconClass="feature-card__icon--purple" title="Mood & Dream Guidance"   description="Turn your current state into tailored practice recommendations — from dream work to mood-led sessions." href={user ? "/mood" : "/login"}      cta={user ? "Check mood" : "Sign in to access"} />
            <FeatureCard icon="🏅" iconClass="feature-card__icon--teal"   title="Badges & Progress"       description="Earn recognition for milestones, consistency, and community contributions. Your journey, made visible."  href={user ? "/gamification" : "/login"} cta={user ? "View badges" : "Sign in to access"} />
            <FeatureCard icon="🎟️" iconClass="feature-card__icon--gold"   title="AekamPass"         description="Manage your access tier and unlock premium content, events, and personalised guidance at every level."   href={user ? "/pass" : "/shop"}       cta={user ? "Manage pass" : "See membership plans"} />
          </div>
        </div>
      </section>

      {/* ── Stories & Media ───────────────────────────────────── */}
      <section className="section section--surface-alt">
        <div className="container">
          <div className="section-head-row">
            <div>
              <span className="eyebrow">Stories &amp; Media</span>
              <h2 className="display-3 section-head-row__title">Explore our content universe</h2>
            </div>
          </div>
          <div className="stories-media-grid">
            <Link to="/blog" className="stories-media-card">
              <div className="stories-media-card__icon">✍️</div>
              <div>
                <h3 className="stories-media-card__title">Blog</h3>
                <p className="stories-media-card__desc">Articles on meditation, dreams, philosophy, and the moksha path.</p>
              </div>
              <span className="stories-media-card__arrow">→</span>
            </Link>
            <Link to="/magazine" className="stories-media-card">
              <div className="stories-media-card__icon">📰</div>
              <div>
                <h3 className="stories-media-card__title">Aekam Magazine</h3>
                <p className="stories-media-card__desc">Our quarterly journal of consciousness, healing, and wisdom traditions.</p>
              </div>
              <span className="stories-media-card__arrow">→</span>
            </Link>
            <Link to="/podcast" className="stories-media-card">
              <div className="stories-media-card__icon">🎙️</div>
              <div>
                <h3 className="stories-media-card__title">Podcast</h3>
                <p className="stories-media-card__desc">Deep conversations on meditation, consciousness, and the spiritual path.</p>
              </div>
              <span className="stories-media-card__arrow">→</span>
            </Link>
            <Link to="/portfolio" className="stories-media-card">
              <div className="stories-media-card__icon">🖼️</div>
              <div>
                <h3 className="stories-media-card__title">Portfolio</h3>
                <p className="stories-media-card__desc">Sacred art, photography, and visual expressions of inner awakening.</p>
              </div>
              <span className="stories-media-card__arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Latest Wisdom ─────────────────────────────────────── */}
      {(loading || state.posts.length > 0) && (
        <section className="section section--surface-alt">
          <div className="container">
            <div className="section-head-row">
              <div>
                <span className="eyebrow">Latest Wisdom</span>
                <h2 className="display-3 section-head-row__title">Fresh teachings for your path</h2>
              </div>
              <Link to="/wisdom" className="btn btn--outline">View all articles →</Link>
            </div>
            {loading ? (
              <p className="text-muted body-sm">Loading articles…</p>
            ) : error ? (
              <p className="alert alert--error">{error}</p>
            ) : (
              <div className="grid-auto">
                {state.posts.slice(0, 6).map((post, idx) => (
                  <article key={post.id} className="post-card">
                    <div className="post-card__image-stub">{EMOJIS[idx % EMOJIS.length]}</div>
                    <div className="post-card__body">
                      <div className="post-card__meta">
                        <span className="post-card__category">Wisdom</span>
                        <time className="post-card__date">{new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</time>
                      </div>
                      <h3 className="post-card__title">{stripHtml(post.title.rendered)}</h3>
                      <p className="post-card__excerpt">{stripHtml(post.excerpt?.rendered || post.content?.rendered || "").slice(0, 160)}</p>
                      <div className="post-card__footer">
                        <Link to={`/wisdom/${post.slug}`} className="post-card__readmore">Read more →</Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Featured Products ─────────────────────────────────── */}
      {state.products.length > 0 && (
        <section className="section section--surface">
          <div className="container">
            <div className="section-head-row">
              <div>
                <span className="eyebrow">Sacred Commerce</span>
                <h2 className="display-3 section-head-row__title">Featured products &amp; passes</h2>
              </div>
              <Link to="/shop" className="btn btn--outline">Browse full catalog →</Link>
            </div>
            <div className="grid-auto--sm">
              {state.products.slice(0, 4).map((product) => (
                <article key={product.id} className="product-card">
                  <div className="product-card__image">🕉️</div>
                  <div className="product-card__body">
                    <h3 className="product-card__title">{product.name}</h3>
                    <p className="product-card__desc">{stripHtml(product.description).slice(0, 100) || "Sacred spiritual product."}</p>
                    <div className="product-card__footer">
                      <span className="product-card__price">${formatPrice(product.price)}</span>
                      <span className={`badge ${product.quantity > 0 ? "badge--green" : "badge--red"}`}>{product.quantity > 0 ? "In stock" : "Sold out"}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA Band ──────────────────────────────────────────── */}
      <section className="cta-band">
        <div className="container cta-band__inner">
          <span className="eyebrow eyebrow--white cta-band__eyebrow">Ready to go deeper?</span>
          <h2 className="display-2 text-white cta-band__title">Begin your journey today</h2>
          <p className="body-lg text-white-70 cta-band__body">
            Join thousands of seekers who have transformed their lives through ancient wisdom and modern practice tools.
          </p>
          <div className="cta-band__ctas">
            <Link to={user ? "/practice" : "/login"} className="btn btn--gold btn--lg">
              {user ? "Continue Practice" : "Create Free Account"}
            </Link>
            <Link to="/wisdom" className="btn btn--outline-light btn--lg">Explore Wisdom Library</Link>
          </div>
        </div>
      </section>
    </>
  );
}

function FeatureCard({ icon, iconClass, title, description, href, cta }: { icon: string; iconClass: string; title: string; description: string; href: string; cta: string; }) {
  return (
    <div className="feature-card">
      <div className={`feature-card__icon ${iconClass}`} aria-hidden="true">{icon}</div>
      <h3 className="feature-card__title">{title}</h3>
      <p className="feature-card__body">{description}</p>
      <Link to={href} className="feature-card__link">{cta} <span aria-hidden="true">→</span></Link>
    </div>
  );
}

function stripHtml(input: string): string {
  return input.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function formatPrice(input: string): string {
  const value = Number(input);
  if (Number.isNaN(value)) return input;
  return value.toFixed(2);
}
